import { useState } from "react";
import AdminButton from "../../../core/admin/adminButton";
import AdminModal from "../../../core/admin/adminModal";
import { useWatches } from "../../../../context/watchProvider";
import AdminToast from "../../../core/admin/adminToast";
import AdminTabs from "../../../core/admin/adminTabs";
import AdminDetails from "../../../core/admin/adminDetails";
import AdminField, { AdminSelect, AdminTextarea } from "../../../core/admin/adminField";
import AdminWatchSpecs from "../../../core/admin/adminWatchSpecs";
import AdminWatchGallery from "../../../core/admin/adminWatchGallery";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { WatchStatusBadge } from "../../../core/admin/adminBadge";
import { statusFields } from "../../../../utils/fields";
import { formatPhoneNumber } from "../../../../utils/functions";
import { IWatch } from "./adminWatches";
import AdminCallout from "../../../core/admin/adminCallout";
import { countWatchInfos, getWatchPendencies, WATCH_INFOS_TOTAL } from "../../../../utils/watchCompleteness";

type Tab = "summary" | "details" | "images" | "advertiser";

export default function WatchModal({ close, editableWatch, refresh }: { close: () => void, editableWatch: IWatch | null, refresh: () => void }) {

    const { updateWatch, getAdvertiser } = useWatches();

    const [tab, setTab] = useState<Tab>("summary");
    const [update, setUpdate] = useState(false);
    const [status, setStatus] = useState(editableWatch?.status);

    const [loadingAdv, setLoadingAdv] = useState(false);
    const [advertiser, setAdvertiser] = useState<{ id: string, email: string, phoneNumber: string, name: string } | null>(null);

    const [isUpdating, setIsUpdating] = useState(false);
    // qual das duas ações (status/comentário) está salvando, para o spinner aparecer no botão certo
    const [updatingAction, setUpdatingAction] = useState<"status" | "comment" | null>(null);
    const [goodToast, setGoodToast] = useState(false);
    const [badToast, setBadToast] = useState(false);

    const [comment, setComment] = useState(editableWatch?.comment);

    const doUpdate = async () => {
        // anúncio incompleto não vai ao catálogo (mesma regra do envio para aprovação)
        if (blockedFromDisplay) {
            return null;
        }
        setIsUpdating(true);
        // if (editableWatch?.status === "sold") {
        //     setBadToast(true);
        //     setIsUpdating(false);
        //     return null;
        // }
        if (editableWatch?.id && status) {
            // o banner só sobrevive se o relógio estava e continua em exibição; saindo do ar (ou voltando
            // a ele com um isBanner antigo esquecido), o destaque é zerado
            const keepsBanner = editableWatch.status === "display" && status === "display";
            const updateCall = await updateWatch(editableWatch?.id, { status: status, ...(keepsBanner ? {} : { isBanner: false }), soldAt: status === "sold" ? new Date() : null });
            if (updateCall) {
                setGoodToast(true);
                setIsUpdating(false);
                setUpdate(false);
                editableWatch.status = status;
                refresh();
                return null;
            }
            setBadToast(true);
            setIsUpdating(false);
        }

    }

    const updateComment = async () => {
        setIsUpdating(true);
        if (editableWatch?.id && (comment || comment === "")) {
            const updateCall = await updateWatch(editableWatch?.id, { comment: comment });
            if (updateCall) {
                setGoodToast(true);
                setIsUpdating(false);
                setUpdate(false);
                editableWatch.comment = comment;
                refresh();
                return null;
            }
            setBadToast(true);
            setIsUpdating(false);
        }
        setIsUpdating(false);
    }

    const getAdvertiserInfo = async () => {
        if (editableWatch?.advertiser) {
            setLoadingAdv(true)
            const adv = await getAdvertiser(editableWatch?.advertiser);
            if (adv) {
                setAdvertiser({ id: adv?.id, name: adv?.name, email: adv?.email, phoneNumber: adv?.phoneNumber });
            }
            setLoadingAdv(false);
        }
    }

    // os dados do anunciante só são buscados quando a aba é aberta (antes: botão que abria outro modal)
    const openTab = (t: Tab) => {
        setTab(t);
        if (t === "advertiser" && !advertiser && !loadingAdv) {
            getAdvertiserInfo();
        }
    }

    const propsCount = countWatchInfos(editableWatch);
    const pendencies = getWatchPendencies(editableWatch);
    const blockedFromDisplay = status === "display" && pendencies.length > 0;

    const cover = editableWatch?.images?.length ? editableWatch.images[0].cImgUrl || editableWatch.images[0].imgUrl : undefined;

    return (
        <AdminModal
            close={close}
            size="lg"
            title={`${editableWatch?.brand || ""} ${editableWatch?.model || ""}`}
            subtitle={<span className="flex flex-wrap items-center gap-2">Ano {editableWatch?.year} <WatchStatusBadge status={editableWatch?.status} /></span>}
            tabs={
                <AdminTabs
                    value={tab}
                    onChange={openTab}
                    tabs={[
                        { value: "summary", label: "Resumo" },
                        { value: "details", label: <>Detalhes <span className="text-xs font-normal text-gray-400">{propsCount}/{WATCH_INFOS_TOTAL}</span></> },
                        { value: "images", label: "Imagens", count: editableWatch?.images?.length || 0 },
                        { value: "advertiser", label: "Anunciante" },
                    ]}
                />
            }
        >
            {
                tab === "summary" ?
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col sm:flex-row gap-5">
                            {
                                cover ?
                                    <button type="button" onClick={() => openTab("images")} className="shrink-0 h-32 w-32 overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-200 hover:ring-secondary transition" aria-label="Ver imagens">
                                        <img src={cover} alt="capa" className="h-full w-full object-cover" />
                                    </button>
                                    : null
                            }
                            <AdminDetails
                                className="flex-1"
                                items={[
                                    { label: "Marca", value: editableWatch?.brand },
                                    { label: "Modelo", value: editableWatch?.model },
                                    { label: "Ano", value: editableWatch?.year },
                                    { label: "ID", value: <span className="font-mono text-xs">{editableWatch?.id}</span> },
                                ]}
                            />
                        </div>

                        <section className="rounded-xl border border-gray-200 p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h3 className="font-bold text-gray-900">Status</h3>
                                    <div className="mt-1"><WatchStatusBadge status={editableWatch?.status} /></div>
                                </div>
                                {
                                    // editableWatch?.status === "sold" ?
                                    //     null :
                                    update ?
                                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                            <AdminSelect value={status} onChange={(e) => setStatus(e.target.value)} className="sm:w-56">
                                                {
                                                    Object.keys(statusFields).map((k, i) => (
                                                        <option key={i} value={k}>{statusFields[k]}</option>
                                                    ))
                                                }
                                            </AdminSelect>
                                            <div className="flex gap-2">
                                                <AdminButton variant="secondary" disabled={isUpdating} onClick={() => setUpdate(false)} className="flex-1">Cancelar</AdminButton>
                                                <AdminButton loading={isUpdating && updatingAction === "status"} disabled={isUpdating || blockedFromDisplay} onClick={() => { setUpdatingAction("status"); doUpdate(); }} className="flex-1">Atualizar</AdminButton>
                                            </div>
                                        </div>
                                        :
                                        <AdminButton variant="secondary" onClick={() => setUpdate(true)}>
                                            Atualizar status
                                        </AdminButton>
                                }
                            </div>
                            {
                                update && blockedFromDisplay ?
                                    <AdminCallout tone="warning" title="Este anúncio ainda não pode ser publicado. Falta:" className="mt-4">
                                        <ul className="list-disc pl-5">
                                            {
                                                pendencies.map(p => <li key={p}>{p}</li>)
                                            }
                                        </ul>
                                        <p className="mt-2">Peça os ajustes ao anunciante pelo comentário abaixo e devolva o anúncio para Rascunho.</p>
                                    </AdminCallout>
                                    : null
                            }
                        </section>

                        <section className="rounded-xl border border-gray-200 p-4">
                            <AdminField label="Comentar / Solicitar alteração" htmlFor="watch-comment" hint="O anunciante vê este comentário na aba “Comentários” do rascunho.">
                                <AdminTextarea id="watch-comment" value={comment || ""} onChange={e => setComment(e.target.value)} placeholder="Nenhum comentário" />
                            </AdminField>
                            <div className="flex justify-end mt-3">
                                <AdminButton variant="secondary" loading={isUpdating && updatingAction === "comment"} disabled={isUpdating} onClick={() => { setUpdatingAction("comment"); updateComment(); }}>Atualizar comentário</AdminButton>
                            </div>
                        </section>
                    </div>
                    : tab === "details" ?
                        propsCount === 0 || !editableWatch ?
                            <AdminEmptyState title="Nenhum detalhe preenchido" description="O anunciante ainda não completou as informações deste relógio." />
                            : <AdminWatchSpecs watch={editableWatch} />
                        : tab === "images" ?
                            <AdminWatchGallery images={editableWatch?.images} />
                            : tab === "advertiser" ?
                                loadingAdv ?
                                    <AdminLoading />
                                    : advertiser ?
                                        <AdminDetails
                                            items={[
                                                { label: "Nome", value: advertiser?.name },
                                                { label: "E-mail", value: advertiser?.email },
                                                { label: "Telefone", value: formatPhoneNumber(advertiser?.phoneNumber) },
                                                { label: "ID do anunciante", value: <span className="font-mono text-xs">{advertiser?.id}</span> },
                                            ]}
                                        />
                                        : <AdminEmptyState title="Anunciante não encontrado" />
                                : null
            }
            <AdminToast toast={goodToast} setToast={setGoodToast} tone="success" message="Relógio atualizado!" />
            <AdminToast toast={badToast} setToast={setBadToast} tone="danger" message="Erro na operação!" />
        </AdminModal>
    )
}
